import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { AddNoteForm } from '@src/notes/AddNoteForm';
import { AddNoteSchema } from '@src/notes/AddNoteSchema';
import { NoteParentType } from '@src/notes/domain/Note';
import { NoteContext } from '@src/notes/domain/NoteContext';
import { NoteAssembler } from '@src/notes/NoteAssembler';
import { NoteContextField } from '@src/notes/NoteFields';
import { ModalOrderDescription } from '@src/orders/components/modal/ModalOrderDescription';
import type { OrderListing } from '@src/orders/domain/OrderList';
import { usePutOnHoldOrder } from '@src/orders/hooks/usePutOnHoldOrder';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type * as yup from 'yup';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
  order: OrderListing;
}

export const PutOnHoldOrderModal = ({
  open,
  onClose,
  onSuccess,
  onError,
  order,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();

  const { putOnHold } = usePutOnHoldOrder({
    onSuccess,
    onError,
  });

  //oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<yup.InferType<typeof AddNoteSchema> | any>({
    resolver: yupResolver(AddNoteSchema),
    values: {
      [NoteContextField.fieldName]: [NoteContext.onHold.valueOf()],
    },
  });

  const confirm: SubmitHandler<yup.InferType<typeof AddNoteSchema>> = (
    data,
  ) => {
    putOnHold({
      orderId: order.id,
      note: NoteAssembler.fromForm(
        user.id,
        { id: order.id, type: NoteParentType.ORDER },
        data,
      ),
    });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={t('orderSummary.putOnHold')}
    >
      <ModalOrderDescription
        clientName={order.client.name}
        createdAt={order.createdAt}
        orderDisplayId={order.displayId}
      />
      <Stack paddingY={theme.spacing['6']}>
        <Typography variant="body2">
          <Trans
            i18nKey="orderSummary.putOnHoldMessage"
            components={{ strong: <strong style={{ fontWeight: 600 }} /> }}
          />
        </Typography>
      </Stack>
      <Stack paddingY={theme.spacing['6']}>
        <AddNoteForm
          control={form.control}
          context={NoteContext.onHold}
          setValue={form.setValue}
        />
        <Divider style={{ marginTop: theme.spacing['6'] }} />
      </Stack>
      <CloseConfirmModalFooter
        onClose={onClose}
        confirm={form.handleSubmit(confirm)}
      />
    </BaseModal>
  );
};
